import React, { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import { MessageSquare, X, Check, Loader2 } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import logger from '@/lib/logger';

// Per-session dismissal lives in sessionStorage so the banner returns on next login.
const DISMISS_KEY = 'sms_consent_banner_dismissed';
// Magic query param (?show_sms_consent=1) lets the legacy-invite email link
// force the banner to render even if the user has dismissed it earlier.
const FORCE_QUERY_PARAM = 'show_sms_consent';

export function SmsConsentBanner() {
  const { user } = useAuth();
  const [show, setShow] = useState(false);
  const [phone, setPhone] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const check = useCallback(async () => {
    if (!user) { setShow(false); return; }
    const forced = typeof window !== 'undefined'
      && new URLSearchParams(window.location.search).get(FORCE_QUERY_PARAM) === '1';
    if (!forced && typeof window !== 'undefined' && sessionStorage.getItem(DISMISS_KEY) === '1') {
      setShow(false);
      return;
    }
    try {
      const { data } = await api.smsConsentStatus();
      if (data?.prompt) {
        setPhone(data.phone || '');
        setShow(true);
      } else {
        setShow(false);
      }
    } catch (err) {
      logger.error(err);
      setShow(false);
    }
  }, [user]);

  useEffect(() => { check(); }, [check]);

  const respond = async (decision) => {
    if (submitting) return;
    setSubmitting(true);
    try {
      await api.smsConsentRespond(decision);
      if (decision === 'opted_in') {
        toast.success('Thanks — you\'re opted in to SMS updates.');
      } else {
        toast.info('Got it — we won\'t text you.');
      }
      setShow(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save preference');
    } finally {
      setSubmitting(false);
    }
  };

  const dismiss = () => {
    try { sessionStorage.setItem(DISMISS_KEY, '1'); } catch { /* private mode */ }
    setShow(false);
  };

  if (!show) return null;

  return (
    <div
      className="bg-gradient-to-r from-[#EFF6FF] to-[#F8FAFF] border-b border-[#0047AB]/20 px-4 py-2.5"
      data-testid="sms-consent-banner"
    >
      <div className="max-w-7xl mx-auto flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
        <div className="flex items-start gap-2 flex-1 min-w-0">
          <MessageSquare className="w-4 h-4 text-[#0047AB] flex-shrink-0 mt-0.5" />
          <p className="text-xs sm:text-sm text-slate-700 leading-snug">
            <span className="font-medium text-slate-900">Stay in the loop via SMS?</span>{' '}
            Texts about lead alerts, account changes, and renewal reminders sent to{' '}
            <span className="font-mono text-[#0047AB]">{phone}</span>. Msg &amp; data rates may apply. Reply STOP anytime.
          </p>
        </div>
        <div className="flex items-center gap-1.5 flex-shrink-0">
          <button
            type="button"
            onClick={() => respond('opted_in')}
            disabled={submitting}
            className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-sm bg-[#0047AB] text-white hover:bg-[#003a8c] disabled:opacity-60 transition-colors"
            data-testid="sms-consent-opt-in-btn"
          >
            {submitting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
            Yes, text me
          </button>
          <button
            type="button"
            onClick={() => respond('declined')}
            disabled={submitting}
            className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-sm border border-slate-300 text-slate-700 hover:bg-white disabled:opacity-60 transition-colors"
            data-testid="sms-consent-decline-btn"
          >
            No thanks
          </button>
          <button
            type="button"
            onClick={dismiss}
            className="p-1.5 text-slate-500 hover:text-slate-900 rounded-sm hover:bg-white/60 transition-colors"
            title="Remind me later"
            data-testid="sms-consent-dismiss-btn"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}
